import { io, Socket } from 'socket.io-client';
import { API_BASE_URL } from './routes';

// WebSocket事件名称常量
export const WS_EVENTS = {
  INIT_CHAT: 'init_chat',
  CHAT_MESSAGE: 'chat_message',
  CHAT_ERROR: 'chat_error',
  CHAT_ARCHIVED: 'chat_archived',
  NOTIFICATION: 'notification',
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  CONNECT_ERROR: 'connect_error',
  ERROR: 'error'
} as const;

interface ChatMessage {
  content: string;
  role: 'user' | 'assistant';
  timestamp: string;
  projectId: string;
  stage: number;
  is_final?: boolean;
  tool?: {
    name: string;
    status: 'calling' | 'success' | 'failed';
    error?: string;
  };
}

interface NotificationMessage {
  type: 'notification';
  content: string;
  notification_type: 'success' | 'info' | 'warning' | 'error';
  timestamp: number;
}

type EventHandler = (...args: any[]) => void;

class SocketService {
  private socket: Socket | null = null;
  private eventHandlers: Map<string, Set<EventHandler>> = new Map();

  connect() {
    if (!this.socket) {
      console.log('[Socket] Base URL:', API_BASE_URL);
      
      // 创建 Socket.IO 连接
      this.socket = io(API_BASE_URL, {
        path: '/socket.io',
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        autoConnect: true,
        forceNew: true
      });

      // 注册默认事件处理器
      this.setupDefaultHandlers();
    }
  }

  private setupDefaultHandlers() {
    if (!this.socket) return;

    this.socket.on(WS_EVENTS.CONNECT, () => {
      console.log('Connected to Socket.IO server');
      this.emit('client_ready');
      this.triggerHandlers(WS_EVENTS.CONNECT);
    });

    this.socket.on(WS_EVENTS.CONNECT_ERROR, (error) => {
      console.error('Socket.IO connection error:', error);
      this.triggerHandlers(WS_EVENTS.CONNECT_ERROR, error);
      
      setTimeout(() => {
        console.log('Attempting to reconnect...');
        if (this.socket) {
          this.socket.connect();
        }
      }, 2000);
    });

    this.socket.on(WS_EVENTS.DISCONNECT, (reason) => {
      console.log('Disconnected from Socket.IO server:', reason);
      this.triggerHandlers(WS_EVENTS.DISCONNECT, reason);
      
      if (reason === 'io server disconnect') {
        this.socket?.connect();
      }
    });

    this.socket.on(WS_EVENTS.CHAT_ERROR, (error) => {
      console.error('Socket.IO error:', error);
      this.triggerHandlers(WS_EVENTS.CHAT_ERROR, error);
    });

    this.socket.on(WS_EVENTS.CHAT_MESSAGE, (message: ChatMessage) => {
      console.log('Received message:', message);
      this.triggerHandlers(WS_EVENTS.CHAT_MESSAGE, message);
    });

    this.socket.on(WS_EVENTS.CHAT_ARCHIVED, (data) => {
      console.log('Chat archived:', data);
      this.triggerHandlers(WS_EVENTS.CHAT_ARCHIVED, data);
    });

    // 添加通知事件处理器
    this.socket.on(WS_EVENTS.NOTIFICATION, (notification: NotificationMessage) => {
      console.log('Received notification:', notification);
      this.triggerHandlers(WS_EVENTS.NOTIFICATION, notification);
    });
  }

  private triggerHandlers(event: string, ...args: any[]) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(...args);
        } catch (error) {
          console.error(`Error in ${event} handler:`, error);
        }
      });
    }
  }

  on(event: string, handler: EventHandler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)?.add(handler);
  }

  off(event: string, handler: EventHandler) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.eventHandlers.delete(event);
      }
    }
  }

  emit(event: string, data?: any) {
    if (!this.socket?.connected) {
      console.warn('Socket is not connected. Attempting to connect...');
      this.connect();
      // 将消息加入队列，连接成功后发送
      this.once('connect', () => {
        this.socket?.emit(event, data);
      });
      return;
    }
    this.socket.emit(event, data);
  }

  once(event: string, handler: EventHandler) {
    const onceHandler = (...args: any[]) => {
      this.off(event, onceHandler);
      handler(...args);
    };
    this.on(event, onceHandler);
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    // 清空所有事件处理器
    this.eventHandlers.clear();
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

export const socketService = new SocketService(); 